import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ContentState, EditorState } from "draft-js";

import NewsletterChunkButton from "editor/components/NewsletterChunkButton";
import Editor from "editor/components/Editor";
import LinkEditor from "editor/components/LinkEditor";
import EmbedEditor from "editor/containers/EmbedEditor";
import NewsletterChunk from "editor/components/NewsletterChunk";

import { onChange, updateEditor } from "editor/actions/form";
import {
  handleKeyCommand,
  handlePastedText,
  handleReturn,
  onTab,
} from "editor/actions/editor";
import {
  appendNewsletterChunk,
  deleteNewsletterChunk,
  moveNewsletterChunkDown,
  moveNewsletterChunkUp,
  updateNewsletterChunk,
} from "editor/actions/newsletter";
import { saveForm } from "editor/actions/status";

import NewsletterHeader from "./NewsletterHeader";
import NewsletterMetadata from "editor/containers/NewsletterMetadata";
import getActiveEditorState from "editor/utils/getActiveEditorState";

import styles from "./NewsletterEditor.css";
import {
  NEWSLETTER_INTRO_PLACEHOLDER,
  NEWSLETTER_OUTRO_PLACEHOLDER,
} from "config/constants/editor";
import { getRubricListAsyncForTypeahead } from "../../actions/metadata";
import { LOCAL_NEWSLETTERS } from "../../../config/constants";

export class NewsletterEditor extends React.Component {
  constructor(props) {
    super(props);
    this.appendNewsletterChunk = this._appendNewsletterChunk.bind(this);
    this.updateNewsletterChunk = this._updateNewsletterChunk.bind(this);
  }

  /**
   * If the form is saving and there are chunks without IDs, we need to prevent
   * reordering the form so that new chunk IDs can be assigned by their index after the save.
   */
  canReorderChunks() {
    const chunksWithoutIds = this.props.editorForm.chunks.filter(
      chunk => !chunk.id
    );
    return !(
      (this.props.isSaving && chunksWithoutIds.length) ||
      this.props.isReadOnly
    );
  }

  _appendNewsletterChunk() {
    this.props.appendNewsletterChunk();
    return this.props.saveForm();
  }

  _updateNewsletterChunk(index, data) {
    this.props.updateNewsletterChunk(index, data);
    return this.props.saveForm();
  }

  /**
   * This function combines newsletter intro, chunks, and outro (which are each an individual EditorState) into one
   * EditorState and passes it down as props so the WordCount component can get wordcount for the entire newsletter,
   * not just the selected chunk.
   */
  composeBlocksFromNewsletter(form) {
    let all_blocks = [];

    // Add the blocks from the intro EditorState to the array.
    if (form.intro) {
      const intro_blocks = form.intro.getCurrentContent().getBlocksAsArray();
      intro_blocks.forEach(block => {
        all_blocks.push(block);
      });
    }

    // Newsletters can have multiple chunks, and each chunk has an EditorState in the blocks field.
    // For each chunk, add each of the chunk's blocks to the array.
    form.chunks.forEach(chunk => {
      const chunk_blocks = chunk.blocks.getCurrentContent().getBlocksAsArray();
      chunk_blocks.forEach(block => {
        all_blocks.push(block);
      });
    });

    // Add the blocks from the outro EditorState to the array.
    if (form.outro) {
      const outro_blocks = form.outro.getCurrentContent().getBlocksAsArray();
      outro_blocks.forEach(block => {
        all_blocks.push(block);
      });
    }

    // Create a ContentState with all the blocks, and use that to create an EditorState.
    let combined_content = ContentState.createFromBlockArray(all_blocks);
    return EditorState.createWithContent(combined_content);
  }

  render() {
    const isHeaderVisible =
      this.props.editorForm.subscription &&
      this.props.editorForm.subscription.name
        ? true
        : false;
    const multiEditorBlockComposition = this.composeBlocksFromNewsletter(
      this.props.editorForm
    );
    const newsletterSlug =
      this.props.editorForm.subscription &&
      this.props.editorForm.subscription.slug;
    const isLocalNewsletter = LOCAL_NEWSLETTERS.indexOf(newsletterSlug) > -1;
    return (
      <div className={styles.root}>
        <div className={styles.top}>
          <div className={styles.container}>
            <LinkEditor />
            <EmbedEditor />
            <NewsletterHeader
              isVisible={isHeaderVisible}
              headerText={
                this.props.editorForm.subscription
                  ? this.props.editorForm.subscription.name
                  : ""
              }
            />
            <Editor
              onChange={this.props.updateEditor("intro")}
              editorState={this.props.editorForm.intro}
              handleKeyCommand={this.props.handleKeyCommand}
              handleReturn={this.props.handleReturn}
              handlePastedText={this.props.handlePastedText}
              onTab={this.props.onTab}
              placeholder={NEWSLETTER_INTRO_PLACEHOLDER}
              dataCy={"intro"}
              readOnly={this.props.isReadOnly}
              multiEditorBlockComposition={multiEditorBlockComposition}
              contentType={this.props.match.params.type}
            />
            {this.props.editorForm.chunks.map((chunk, index) => {
              return (
                <NewsletterChunk
                  key={chunk.tempKey || chunk.id}
                  chunk={chunk}
                  index={index}
                  chunkCount={this.props.editorForm.chunks.length}
                  onChange={this.props.onChange}
                  updateEditor={this.props.updateEditor}
                  handleKeyCommand={this.props.handleKeyCommand}
                  handleReturn={this.props.handleReturn}
                  handlePastedText={this.props.handlePastedText}
                  onTab={this.props.onTab}
                  updateNewsletterChunk={this.updateNewsletterChunk}
                  moveNewsletterChunkUp={this.props.moveNewsletterChunkUp}
                  moveNewsletterChunkDown={this.props.moveNewsletterChunkDown}
                  deleteNewsletterChunk={this.props.deleteNewsletterChunk}
                  canReorderChunks={this.canReorderChunks()}
                  getBaseUrl={this.props.getBaseUrl}
                  isReadOnly={this.props.isReadOnly}
                  multiEditorBlockComposition={multiEditorBlockComposition}
                  getRubricListAsyncForTypeahead={
                    this.props.getRubricListAsyncForTypeahead
                  }
                  isChunkActive={
                    [`chunks.${index}.blocks`].indexOf(
                      this.props.editorStatus.activeEditor.name
                    ) > -1
                  }
                  isLocalNewsletter={isLocalNewsletter}
                />
              );
            })}
            <NewsletterChunkButton
              onClick={this.appendNewsletterChunk}
              isDisabled={this.props.isReadOnly}
            />

            <Editor
              onChange={this.props.updateEditor("outro")}
              editorState={this.props.editorForm.outro}
              handleKeyCommand={this.props.handleKeyCommand}
              handleReturn={this.props.handleReturn}
              handlePastedText={this.props.handlePastedText}
              onTab={this.props.onTab}
              placeholder={NEWSLETTER_OUTRO_PLACEHOLDER}
              dataCy={"outro"}
              readOnly={this.props.isReadOnly}
              multiEditorBlockComposition={multiEditorBlockComposition}
            />
          </div>
        </div>
        <div className={classNames(styles.container, styles.bottom)}>
          <NewsletterMetadata
            getBaseUrl={this.props.getBaseUrl}
            id={this.props.match.params.id}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    editorForm: state.editor.form,
    activeEditor: getActiveEditorState(state),
    isSaving: state.editor.status.isSaving,
    isReadOnly: state.editor.status.isReadOnly,
    editorStatus: state.editor.status,
  };
};

const mapDispatchToProps = {
  updateEditor,
  handleKeyCommand,
  onTab,
  handleReturn,
  handlePastedText,
  appendNewsletterChunk,
  onChange,
  updateNewsletterChunk,
  moveNewsletterChunkUp,
  moveNewsletterChunkDown,
  deleteNewsletterChunk,
  saveForm,
  getRubricListAsyncForTypeahead,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(NewsletterEditor)
);

NewsletterEditor.propTypes = {
  updateEditor: PropTypes.func.isRequired,
  editorForm: PropTypes.object.isRequired,
  activeEditor: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  handleKeyCommand: PropTypes.func.isRequired,
  handleReturn: PropTypes.func.isRequired,
  handlePastedText: PropTypes.func.isRequired,
  onTab: PropTypes.func.isRequired,
  getBaseUrl: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};
